import { useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { safeReturn } from '@hlt/web-kit/config';
import { routerHref } from '@hlt/web-kit/shell';

/**
 * Where to continue after sign-in: a platform path or an absolute URL on a configured HLT app origin
 * (a tool that sent the person here). Anything else returns home, so sign-in is never an open redirect.
 */
export const safeNext = (next: string | undefined): string => safeReturn(next, '/');

const challengeKey = 'hlt.mfaChallenge';
export const storeChallenge = (id: string) => sessionStorage.setItem(challengeKey, id);
export const readChallenge = (): string | null => sessionStorage.getItem(challengeKey);
export const clearChallenge = () => sessionStorage.removeItem(challengeKey);

/** Read a single-use token from the URL fragment and remove it from browser history. */
export function takeFragmentToken(): string | null {
    const hash = window.location.hash.replace(/^#/, '');
    const token = new URLSearchParams(hash).get('token');
    if (token) window.history.replaceState(null, '', window.location.pathname + window.location.search);
    return token;
}

/**
 * Continue after an identity step without a full page load when the target is in this app (the shared
 * auth frame and its artwork stay on screen); another HLT app is opened with a normal page load.
 */
export function useContinue(): (next: string | undefined) => void {
    const navigate = useNavigate();
    return useCallback(
        (next: string | undefined) => {
            const target = safeNext(next);
            if (target.startsWith('/')) void navigate({ href: routerHref(target), replace: true });
            else window.location.assign(target);
        },
        [navigate],
    );
}
